import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button } from '@/components/atoms';
import { Pagination } from '@/components/molecules';
import { ReportFilters, ReportSummaryCards, ReportTable } from '@/components/organisms/Reports';
import type { TableColumn } from '@/components/organisms/Reports';
import { useCustomersReport, useExportReportMutation, useWarehousesList } from '@/hooks/useErpApi';
import type { CustomerReportFilter, CustomerReportRecord } from '@kisanlink/erp-service';

export default function CustomersReportPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<CustomerReportFilter>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  
  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;
  const {
    data: report,
    isLoading,
    error,
    refetch,
  } = useCustomersReport({ ...filters, limit: itemsPerPage, offset });
  const { data: warehouses } = useWarehousesList({});
  const { mutateAsync: exportReport, isLoading: isExporting } = useExportReportMutation();

  const warehouseOptions = (warehouses || []).map((w) => ({ id: w.id, name: w.name }));

  const handleFilterChange = useCallback((newFilters: Record<string, unknown>) => {
    setFilters(newFilters as CustomerReportFilter);
    setCurrentPage(1);
  }, []);
  
  // Get total count from API response if available
  const totalItems = report?.pagination?.total || (report?.records?.length || 0);

  const handleExport = useCallback(
    async (format: 'xlsx' | 'pdf') => {
      try {
        const blob = await exportReport({
          reportType: 'customers',
          filters: { ...filters, format },
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `customers-report.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Export failed:', err);
      }
    },
    [filters, exportReport]
  );

  const columns: TableColumn<CustomerReportRecord>[] = [
    {
      key: 'customer_id',
      header: 'Customer ID',
      render: (row) => (
        <span className="font-mono text-sm text-neutral-900">{row.customer_id}</span>
      ),
    },
    {
      key: 'warehouse_name',
      header: 'Warehouse',
      render: (row) => (
        <div>
          <div className="text-sm text-neutral-900">{row.warehouse_name}</div>
          <div className="text-xs text-neutral-500">{row.warehouse_id}</div>
        </div>
      ),
    },
    {
      key: 'total_purchases',
      header: 'Total Purchases',
      render: (row) => row.total_purchases.toLocaleString(),
    },
    {
      key: 'purchase_count',
      header: 'Transactions',
      render: (row) => row.purchase_count.toLocaleString(),
    },
    {
      key: 'total_amount',
      header: 'Total Spent',
      render: (row) => (
        <span className="font-medium text-neutral-900">₹{row.total_amount.toLocaleString()}</span>
      ),
    },
    {
      key: 'last_purchase_date',
      header: 'Last Purchase',
      render: (row) =>
        row.last_purchase_date ? new Date(row.last_purchase_date).toLocaleDateString() : '-',
    },
  ];

  const summary = report?.summary;
  const summaryCards = summary
    ? [
        {
          label: 'Total Customers',
          value: summary.total_customers,
          icon: 'Users' as const,
          color: 'primary' as const,
          format: 'number' as const,
        },
        {
          label: 'Total Revenue',
          value: summary.total_revenue,
          icon: 'IndianRupee' as const,
          color: 'success' as const,
          format: 'currency' as const,
        },
        {
          label: 'Avg Purchase Value',
          value: summary.average_purchase_value,
          icon: 'TrendingUp' as const,
          color: 'info' as const,
          format: 'currency' as const,
        },
        {
          label: 'Revenue per Customer',
          value: summary.total_customers > 0 ? summary.total_revenue / summary.total_customers : 0,
          icon: 'UserCheck' as const,
          color: 'warning' as const,
          format: 'currency' as const,
        },
      ]
    : [];


  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
            <Icon name="ArrowLeft" size={16} className="mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Customers Report</h1>
            <p className="text-neutral-600 text-sm mt-1">
              Customer purchase history with revenue metrics
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <ReportFilters
        showDateRange={true}
        showSearch={true}
        showWarehouse={true}
        warehouses={warehouseOptions}
        searchPlaceholder="Search by customer ID..."
        onFilterChange={handleFilterChange}
        onExport={handleExport}
        isExporting={isExporting}
      />

      {/* Summary Cards */}
      {!isLoading && !error && summary && <ReportSummaryCards cards={summaryCards} />}

      {/* Report Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <ReportTable
          columns={columns}
          data={report?.records || []}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          emptyMessage="No customers found matching your criteria"
        />
        {!isLoading && !error && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            disabled={isLoading}
          />
        )}
      </div>
    </div>
  );
}
